// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"

// Background pattern controller — dots, grid, lines, or none.
// Tracks viewport to keep pattern aligned with the pan/zoom.
export default class extends Controller {
  static outlets = ["ui--flow"]

  static values = {
    variant: { type: String, default: "dots" },
    gap: { type: Number, default: 20 },
    // M6: theme token, never hex — a faint muted-foreground dot/grid line.
    // UX audit: default alpha 0.45 → 0.35; grid/lines render fainter still
    // (0.15) through the substitution in _patternColor.
    color: { type: String, default: "hsl(var(--muted-foreground) / 0.35)" },
    size: { type: Number, default: 1 }
  }

  connect() {
    this._render()
    this._onViewport = () => this._updateOffset()
    document.addEventListener("ui--flow:viewportchange", this._onViewport)
  }

  disconnect() {
    document.removeEventListener("ui--flow:viewportchange", this._onViewport)
  }

  _render() {
    const v = this.variantValue, g = this.gapValue, s = this.sizeValue
    const c = this._patternColor(v)
    this.element.style.backgroundImage = "none"
    if (v === "dots") {
      this.element.style.backgroundImage = `radial-gradient(circle, ${c} ${s}px, transparent ${s}px)`
    } else if (v === "grid") {
      this.element.style.backgroundImage = `linear-gradient(to right, ${c} ${s}px, transparent ${s}px), linear-gradient(to bottom, ${c} ${s}px, transparent ${s}px)`
    } else if (v === "lines") {
      this.element.style.backgroundImage = `linear-gradient(to right, ${c} ${s}px, transparent ${s}px)`
    }
    this._updateOffset()
  }

  // Per-variant alpha (UX audit): dots render at the default's 0.35, grid and
  // lines at 0.15. The substitution applies ONLY when the color is exactly
  // the default token string (identity check) — a host-passed explicit color
  // renders verbatim. Mirrored server-side in background_component.rb.
  _patternColor(variant) {
    const c = this.colorValue
    if (c !== "hsl(var(--muted-foreground) / 0.35)") return c
    return variant === "dots" ? c : c.replace(/\/ 0\.35\)$/, "/ 0.15)")
  }

  _updateOffset() {
    const flow = this.uiFlowOutlet
    if (!flow) return
    const z = flow.viewport.zoom
    const gap = this.gapValue * z
    this.element.style.backgroundPosition = `${flow.viewport.x % gap}px ${flow.viewport.y % gap}px`
    this.element.style.backgroundSize = `${gap}px ${gap}px`
  }

  changeVariant(event) {
    this.variantValue = event.currentTarget.dataset.variant
    this._render()
  }
}
